"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const profileFields = [
{ label: "Name & Breed", icon: "TagIcon", value: "Luna · Golden Retriever" },
{ label: "Age & Weight", icon: "ScaleIcon", value: "3 yrs · 28 kg" },
{ label: "Microchip ID", icon: "FingerPrintIcon", value: "985141003123456" },
{ label: "Allergies", icon: "ExclamationTriangleIcon", value: "Chicken protein", color: "#D97706" },
{ label: "Vaccinations", icon: "ShieldCheckIcon", value: "All up to date ✓", color: "#22C55E" },
{ label: "Vet", icon: "BuildingOffice2Icon", value: "Riverside Animal Clinic" }];


export default function PetProfileFeature() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 90);
            });
          }
        });
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Visual profile card mockup */}
          <div className="scroll-fade-left relative">
            <div className="feature-card p-6 max-w-[420px] mx-auto lg:mx-0">
              {/* Pet avatar */}
              <div className="flex items-center gap-4 mb-6 pb-5 border-b border-[#E2EDED] dark:border-slate-700">
                <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0">
                  <AppImage
                    src="https://images.unsplash.com/photo-1725022717680-2706735ac302"
                    alt="Golden retriever dogfob profile photo for DogFob pet profile card"
                    width={64}
                    height={64}
                    className="w-full h-full object-cover" />
                  
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-[#1A2B2B] dark:text-slate-100 font-heading">Luna</h3>
                  <p className="text-sm text-[#4A6363] dark:text-slate-400">Golden Retriever · Female</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="status-dot w-[6px] h-[6px]" />
                    <span className="text-[11px] font-semibold text-[#22C55E]">All vaccines current</span>
                  </div>
                </div>
                <div className="ml-auto">
                  <span className="teal-badge text-[10px]">Profile complete</span>
                </div>
              </div>

              {/* Profile fields */}
              <div className="grid grid-cols-2 gap-3">
                {profileFields.map((field) =>
                <div key={field.label} className="bg-[#F7F8F6] dark:bg-slate-700/50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon name={field.icon as "TagIcon"} size={12} className="text-[#8AA4A4] dark:text-slate-500" />
                      <span className="text-[10px] text-[#8AA4A4] dark:text-slate-500 font-medium uppercase tracking-wide">{field.label}</span>
                    </div>
                    <p
                    className="text-xs font-bold font-heading"
                    style={{ color: field.color || "#1A2B2B" }}>
                    
                      {field.value}
                    </p>
                  </div>
                )}
              </div>

              {/* Add pet CTA */}
              <button className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#E2EDED] dark:border-slate-600 text-sm text-[#8AA4A4] dark:text-slate-500 hover:border-teal-300 hover:text-teal-500 transition-all duration-200">
                <Icon name="PlusCircleIcon" size={16} />
                Add another pet
              </button>
            </div>

            {/* Floating document card */}
            <div
              className="absolute -top-5 -right-4 glass-card rounded-xl px-4 py-3 animate-float-c max-w-[160px]"
              style={{ boxShadow: "0 12px 36px rgba(15,32,39,0.1)" }}>
              
              <Icon name="DocumentTextIcon" size={16} className="text-teal-500 mb-1" />
              <p className="text-[11px] font-bold text-[#1A2B2B] dark:text-slate-100 font-heading">Vet records stored</p>
              <p className="text-[10px] text-[#8AA4A4] dark:text-slate-500">12 documents</p>
            </div>
          </div>

          {/* Right: Copy */}
          <div className="flex flex-col gap-6 scroll-fade-right">
            <span className="teal-badge">Pet Profile</span>
            <h2 className="text-4xl sm:text-[42px] font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight leading-tight">
              A complete health record
              <br />
              <span className="gradient-text">your vet will love.</span>
            </h2>
            <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-lg">
              Your pet's profile stores everything a vet, groomer, or sitter might need — and you share it with a single tap. No more repeating the same information at every appointment.
            </p>

            <ul className="flex flex-col gap-3">
              {[
              "Name, breed, age, weight, and color",
              "Complete vaccine history with due-date tracking",
              "Known allergies, medications, and special needs",
              "Microchip ID and registration number",
              "Uploaded vet records and health documents",
              "Unlimited pets — dogfobs, cats, rabbits, and more"].
              map((item) =>
              <li key={item} className="flex items-start gap-3 text-[#4A6363] dark:text-slate-400 text-sm">
                  <Icon name="CheckCircleIcon" size={18} className="text-teal-500 flex-shrink-0 mt-0.5" variant="solid" />
                  {item}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>);

}